import { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Button, StatCard } from '../components/common';
import {
  getGraphQLClient,
  GET_AI_PROMPT_LOGS,
  GET_AI_PROMPT_LOG_DETAIL,
  GET_AI_LOGS_GROUPED,
} from '../lib/graphql';

interface AiPromptLog {
  id: string;
  requestType: string;
  bookId: string | null;
  bookTitle: string | null;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  inputPrompt: string | null;
  outputResponse: string | null;
  modelUsed: string | null;
  durationMs: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  estimatedCost: number | null;
  outputWordCount: number | null;
  errorMessage: string | null;
  success: boolean;
  createdAt: string;
}

interface AiPromptLogsStats {
  total: number;
  today: number;
  failed: number;
  avgDurationMs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  estimatedCost: number;
  byType: Array<{ requestType: string; count: number }>;
}

interface Book {
  id: string;
  title: string;
}

interface User {
  id: string;
  name: string | null;
  email: string;
}

interface GroupedStats {
  groupId: string;
  groupName: string;
  groupType: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  avgDurationMs: number;
  failedCount: number;
  estimatedCost: number;
}

interface AiPromptLogsData {
  aiPromptLogs: AiPromptLog[];
  aiPromptLogsStats: AiPromptLogsStats;
  aiPromptLogRequestTypes: string[];
  booksWithLogs: Book[];
  usersWithLogs: User[];
}

interface AiLogsGroupedData {
  aiLogsGroupedByUser: GroupedStats[];
  aiLogsGroupedByType: GroupedStats[];
  aiLogsGroupedByBook: GroupedStats[];
}

interface AiPromptLogDetailData {
  aiPromptLog: AiPromptLog | null;
}

type InputFormat = 'raw' | 'markdown';
type OutputFormat = 'raw' | 'json' | 'markdown';
type ViewMode = 'list' | 'byUser' | 'byType' | 'byBook';
type TimePeriod = 'today' | 'last7Days' | 'last30Days' | 'allTime' | 'custom';
type CopiedState = 'input' | 'output' | 'both' | null;

// Helper to format date as YYYY-MM-DD for GraphQL
const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Helper to get date range from time period
const getDateRange = (
  period: TimePeriod,
  customStart?: string,
  customEnd?: string
): { startDate: string | null; endDate: string | null } => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  switch (period) {
    case 'today': {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'last7Days': {
      const start = new Date();
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'last30Days': {
      const start = new Date();
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      return { startDate: formatDate(start), endDate: formatDate(today) };
    }
    case 'custom':
      return { startDate: customStart || null, endDate: customEnd || null };
    case 'allTime':
    default:
      return { startDate: null, endDate: null };
  }
};

// Component to render formatted content
function FormattedContent({
  content,
  format,
}: {
  content: string | null;
  format: InputFormat | OutputFormat;
}) {
  const htmlContent = useMemo(() => {
    if (!content || format !== 'markdown') return '';
    return marked.parse(content, { async: false, gfm: true, breaks: true }) as string;
  }, [content, format]);

  if (!content) return <span className="text-gray-400">-</span>;

  if (format === 'raw') {
    return (
      <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-mono">
        {content}
      </pre>
    );
  }

  if (format === 'json') {
    let parsed;
    try {
      parsed = JSON.parse(content);
    } catch {
      // If JSON parsing fails, fall back to raw display
      parsed = null;
    }

    const displayContent = parsed !== null ? JSON.stringify(parsed, null, 2) : content;

    return (
      <pre className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-mono">
        {displayContent}
      </pre>
    );
  }

  // markdown format - render markdown to HTML using marked
  return (
    <div
      className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg text-sm overflow-x-auto max-h-96 overflow-y-auto prose dark:prose-invert prose-sm max-w-none prose-pre:bg-gray-200 dark:prose-pre:bg-gray-800 prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-headings:mt-4 prose-headings:mb-2"
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}

// Format tokens for display
function formatTokens(tokens: number | null): string {
  if (tokens === null || tokens === undefined) return '-';
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(2)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return tokens.toLocaleString();
}

// Grouped stats table component
function GroupedStatsTable({
  data,
  groupLabel,
  onGroupClick,
}: {
  data: GroupedStats[];
  groupLabel: string;
  onGroupClick?: (groupId: string) => void;
}) {
  const { t } = useTranslation();

  if (!data.length) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('admin.noLogs')}</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-900">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {groupLabel}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.requests')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.input')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.output')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.totalTokens')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.estimatedCost')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.avgDuration')}
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('admin.failedCount')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((item) => (
            <tr
              key={item.groupId}
              className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${onGroupClick ? 'cursor-pointer' : ''}`}
              onClick={() => onGroupClick?.(item.groupId)}
            >
              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 max-w-[250px] truncate">
                {item.groupName}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                {item.requestCount.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-blue-600 dark:text-blue-400 text-right font-mono">
                {formatTokens(item.inputTokens)}
              </td>
              <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 text-right font-mono">
                {formatTokens(item.outputTokens)}
              </td>
              <td className="px-4 py-3 text-sm text-purple-600 dark:text-purple-400 text-right font-mono font-medium">
                {formatTokens(item.totalTokens)}
              </td>
              <td className="px-4 py-3 text-sm text-amber-600 dark:text-amber-400 text-right font-mono">
                ${item.estimatedCost.toFixed(4)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                {item.avgDurationMs.toLocaleString()}ms
              </td>
              <td className="px-4 py-3 text-sm text-right">
                {item.failedCount > 0 ? (
                  <span className="text-red-600 dark:text-red-400">{item.failedCount}</span>
                ) : (
                  <span className="text-gray-400">0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AiPromptLogsPage() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    (searchParams.get('view') as ViewMode) || 'list'
  );

  // Time period state
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(() =>
    (searchParams.get('period') as TimePeriod) || 'last30Days'
  );
  const [customStartDate, setCustomStartDate] = useState<string>(() =>
    searchParams.get('startDate') || ''
  );
  const [customEndDate, setCustomEndDate] = useState<string>(() =>
    searchParams.get('endDate') || ''
  );
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(() =>
    searchParams.get('period') === 'custom'
  );

  // AI logs state
  const [logsData, setLogsData] = useState<AiPromptLogsData | null>(null);
  const [groupedData, setGroupedData] = useState<AiLogsGroupedData | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AiPromptLog | null>(null);
  const [logDetailLoading, setLogDetailLoading] = useState(false);
  const [filterBookId, setFilterBookId] = useState<string>(() =>
    searchParams.get('bookId') || ''
  );
  const [filterUserId, setFilterUserId] = useState<string>(() =>
    searchParams.get('userId') || ''
  );
  const [filterRequestType, setFilterRequestType] = useState<string>(() =>
    searchParams.get('type') || ''
  );
  const [filterModelUsed, setFilterModelUsed] = useState<string>(() =>
    searchParams.get('model') || ''
  );
  const [filterSuccess, setFilterSuccess] = useState<string>(() =>
    searchParams.get('success') || ''
  );
  const [inputFormat, setInputFormat] = useState<InputFormat>('raw');
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('raw');
  const [copiedState, setCopiedState] = useState<CopiedState>(null);
  const LOGS_LIMIT = 20;

  // Sync filters to URL search params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (viewMode !== 'list') params.view = viewMode;
    if (timePeriod !== 'last30Days') params.period = timePeriod;
    if (timePeriod === 'custom' && customStartDate) params.startDate = customStartDate;
    if (timePeriod === 'custom' && customEndDate) params.endDate = customEndDate;
    if (filterBookId) params.bookId = filterBookId;
    if (filterUserId) params.userId = filterUserId;
    if (filterRequestType) params.type = filterRequestType;
    if (filterModelUsed) params.model = filterModelUsed;
    if (filterSuccess) params.success = filterSuccess;
    // Preserve logId if a log is selected
    const logId = searchParams.get('logId');
    if (logId) params.logId = logId;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when filter values change, not searchParams
  }, [viewMode, timePeriod, customStartDate, customEndDate, filterBookId, filterUserId, filterRequestType, filterModelUsed, filterSuccess]);

  // Get current date range
  const dateRange = useMemo(() => {
    return getDateRange(timePeriod, customStartDate, customEndDate);
  }, [timePeriod, customStartDate, customEndDate]);

  // Auto-detect output format based on content
  const detectOutputFormat = (content: string | null): OutputFormat => {
    if (!content) return 'raw';
    const trimmed = content.trim();

    // Try to parse as JSON to validate it's actually JSON
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        JSON.parse(trimmed);
        return 'json';
      } catch {
        // Not valid JSON, fallback to markdown
        return 'markdown';
      }
    }

    return 'markdown';
  };

  // Copy to clipboard with visual feedback
  const copyToClipboard = async (type: CopiedState) => {
    if (!selectedLog) return;

    let text = '';
    if (type === 'input') {
      text = selectedLog.inputPrompt || '';
    } else if (type === 'output') {
      text = selectedLog.outputResponse || '';
    } else if (type === 'both') {
      text = `input:\n\`\`\`\n${selectedLog.inputPrompt || ''}\n\`\`\`\n\noutput:\n\`\`\`\n${selectedLog.outputResponse || ''}\n\`\`\``;
    }

    await navigator.clipboard.writeText(text);
    setCopiedState(type);
    setTimeout(() => setCopiedState(null), 2000);
  };

  // Fetch AI logs
  const fetchLogs = useCallback(
    async (append = false) => {
      if (!user?.admin) return;

      setLogsLoading(true);
      try {
        const client = getGraphQLClient();
        const data = await client.request<AiPromptLogsData>(GET_AI_PROMPT_LOGS, {
          bookId: filterBookId || null,
          userId: filterUserId || null,
          requestType: filterRequestType || null,
          modelUsed: filterModelUsed || null,
          success: filterSuccess === '' ? null : filterSuccess === 'true',
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: LOGS_LIMIT,
          offset: append && logsData ? logsData.aiPromptLogs.length : 0,
        });

        if (append && logsData) {
          setLogsData({
            ...data,
            aiPromptLogs: [...logsData.aiPromptLogs, ...data.aiPromptLogs],
          });
        } else {
          setLogsData(data);
        }
      } catch (error) {
        console.error('Failed to fetch AI logs:', error);
      } finally {
        setLogsLoading(false);
      }
    },
    [user?.admin, filterBookId, filterUserId, filterRequestType, filterModelUsed, filterSuccess, dateRange, logsData]
  );

  // Fetch grouped data
  const fetchGroupedData = useCallback(async () => {
    if (!user?.admin) return;

    setLogsLoading(true);
    try {
      const client = getGraphQLClient();
      const data = await client.request<AiLogsGroupedData>(GET_AI_LOGS_GROUPED, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      setGroupedData(data);
    } catch (error) {
      console.error('Failed to fetch grouped AI logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [user?.admin, dateRange]);

  // Fetch single log detail
  const fetchLogDetail = useCallback(
    async (logId: string, updateUrl = true) => {
      setLogDetailLoading(true);
      try {
        const client = getGraphQLClient();
        const data = await client.request<AiPromptLogDetailData>(GET_AI_PROMPT_LOG_DETAIL, {
          id: logId,
        });
        setSelectedLog(data.aiPromptLog);
        // Auto-detect output format when loading log
        if (data.aiPromptLog?.outputResponse) {
          setOutputFormat(detectOutputFormat(data.aiPromptLog.outputResponse));
        }
        // Update URL with log ID for easy navigation/bookmarking
        if (updateUrl && data.aiPromptLog) {
          setSearchParams(prev => {
            prev.set('logId', logId);
            return prev;
          });
        }
      } catch (error) {
        console.error('Failed to fetch log detail:', error);
      } finally {
        setLogDetailLoading(false);
      }
    },
    [setSearchParams]
  );

  // Fetch logs when filters change
  useEffect(() => {
    if (user?.admin) {
      if (viewMode === 'list') {
        fetchLogs(false);
      } else {
        fetchGroupedData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchLogs intentionally excluded to avoid loops from logsData dependency
  }, [
    user?.admin,
    filterBookId,
    filterUserId,
    filterRequestType,
    filterModelUsed,
    filterSuccess,
    dateRange,
    viewMode,
    fetchGroupedData,
  ]);

  // Sync selectedLog with URL parameter (handles browser back/forward)
  useEffect(() => {
    const logIdFromUrl = searchParams.get('logId');
    if (logIdFromUrl && user?.admin && !selectedLog) {
      fetchLogDetail(logIdFromUrl, false);
    } else if (!logIdFromUrl && selectedLog) {
      setSelectedLog(null);
    }
  }, [searchParams, user?.admin, selectedLog, fetchLogDetail]);

  // Handle time period change
  const handleTimePeriodChange = (period: TimePeriod) => {
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
    }
    setTimePeriod(period);
  };

  // Handle applying custom date range
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setTimePeriod('custom');
    }
  };

  // Handle group click to filter list view
  const handleGroupClick = (groupType: string, groupId: string) => {
    setViewMode('list');
    if (groupType === 'user') {
      setFilterUserId(groupId);
    } else if (groupType === 'type') {
      setFilterRequestType(groupId);
    } else if (groupType === 'book') {
      setFilterBookId(groupId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect non-admin users
  if (!user?.admin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin"
          className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('admin.title')}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('admin.aiPromptLogs')}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('admin.aiPromptLogsDescription')}
        </p>
      </div>

      {/* Time Period Filter */}
      <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('admin.timePeriod')}:
          </span>
          <div className="flex flex-wrap gap-2">
            {(['today', 'last7Days', 'last30Days', 'allTime', 'custom'] as TimePeriod[]).map(
              (period) => (
                <button
                  key={period}
                  onClick={() => handleTimePeriodChange(period)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    timePeriod === period
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t(`admin.${period}`)}
                </button>
              )
            )}
          </div>

          {/* Custom date picker */}
          {showCustomDatePicker && (
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
              />
              <span className="text-gray-500">-</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg"
              />
              <button
                onClick={handleApplyCustomRange}
                disabled={!customStartDate || !customEndDate}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('admin.apply')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6">
        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg inline-flex">
          {(['list', 'byUser', 'byType', 'byBook'] as ViewMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                viewMode === mode
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              {t(`admin.${mode === 'list' ? 'listView' : mode}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards - Only show in list view */}
      {viewMode === 'list' && logsData?.aiPromptLogsStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <StatCard
            value={logsData.aiPromptLogsStats.total.toLocaleString()}
            label={t('admin.totalRequests')}
          />
          <StatCard
            value={logsData.aiPromptLogsStats.failed.toLocaleString()}
            label={t('admin.failedRequests')}
            color="red"
          />
          <StatCard
            value={formatTokens(logsData.aiPromptLogsStats.totalInputTokens)}
            label={t('admin.input')}
            color="blue"
          />
          <StatCard
            value={formatTokens(logsData.aiPromptLogsStats.totalOutputTokens)}
            label={t('admin.output')}
            color="green"
          />
          <StatCard
            value={formatTokens(logsData.aiPromptLogsStats.totalTokens)}
            label={t('admin.totalTokens')}
            color="purple"
          />
          <StatCard
            value={`$${logsData.aiPromptLogsStats.estimatedCost.toFixed(4)}`}
            label={t('admin.estimatedCost')}
            color="amber"
          />
          <StatCard
            value={`${logsData.aiPromptLogsStats.avgDurationMs.toLocaleString()}ms`}
            label={t('admin.avgDuration')}
            color="gray"
          />
        </div>
      )}

      {/* Log Detail View */}
      {selectedLog && (
        <div className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => {
                setSelectedLog(null);
                setSearchParams(prev => {
                  prev.delete('logId');
                  return prev;
                });
              }}
              className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {t('admin.backToLogs')}
            </button>
            <button
              onClick={() => copyToClipboard('both')}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              {copiedState === 'both' ? t('common.copied') : t('admin.copyAll')}
            </button>
          </div>

          {logDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.requestType')}
                  </div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedLog.requestType}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.status')}
                  </div>
                  <div
                    className={`font-medium ${selectedLog.success ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {selectedLog.success ? t('admin.successful') : t('admin.failed')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.modelUsed')}
                  </div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedLog.modelUsed || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.duration')}
                  </div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedLog.durationMs ? `${selectedLog.durationMs.toLocaleString()}ms` : '-'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.inputTokens')}
                  </div>
                  <div className="font-medium text-blue-600 dark:text-blue-400">
                    {selectedLog.inputTokens?.toLocaleString() || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.outputTokens')}
                  </div>
                  <div className="font-medium text-green-600 dark:text-green-400">
                    {selectedLog.outputTokens?.toLocaleString() || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.totalTokens')}
                  </div>
                  <div className="font-medium text-purple-600 dark:text-purple-400">
                    {selectedLog.totalTokens?.toLocaleString() || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.estimatedCost')}
                  </div>
                  <div className="font-medium text-amber-600 dark:text-amber-400">
                    {selectedLog.estimatedCost ? `$${selectedLog.estimatedCost.toFixed(6)}` : '-'}
                  </div>
                </div>
              </div>

              {selectedLog.userName && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.user')}</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedLog.userName} ({selectedLog.userEmail})
                  </div>
                </div>
              )}

              {selectedLog.bookTitle && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('admin.book')}</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedLog.bookTitle}
                  </div>
                </div>
              )}

              {selectedLog.errorMessage && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.errorMessage')}
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300 text-sm font-mono">
                    {selectedLog.errorMessage}
                  </div>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.inputPrompt')}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard('input')}
                      className="px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-1"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      {copiedState === 'input' ? t('common.copied') : t('common.copy')}
                    </button>
                    <button
                      onClick={() => setInputFormat('raw')}
                      className={`px-2 py-1 text-xs rounded ${
                        inputFormat === 'raw'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Raw
                    </button>
                    <button
                      onClick={() => setInputFormat('markdown')}
                      className={`px-2 py-1 text-xs rounded ${
                        inputFormat === 'markdown'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Markdown
                    </button>
                  </div>
                </div>
                <FormattedContent content={selectedLog.inputPrompt} format={inputFormat} />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('admin.outputResponse')}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard('output')}
                      className="px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center gap-1"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      {copiedState === 'output' ? t('common.copied') : t('common.copy')}
                    </button>
                    <button
                      onClick={() => setOutputFormat('raw')}
                      className={`px-2 py-1 text-xs rounded ${
                        outputFormat === 'raw'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Raw
                    </button>
                    <button
                      onClick={() => setOutputFormat('json')}
                      className={`px-2 py-1 text-xs rounded ${
                        outputFormat === 'json'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      JSON
                    </button>
                    <button
                      onClick={() => setOutputFormat('markdown')}
                      className={`px-2 py-1 text-xs rounded ${
                        outputFormat === 'markdown'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Markdown
                    </button>
                  </div>
                </div>
                <FormattedContent content={selectedLog.outputResponse} format={outputFormat} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grouped Views */}
      {!selectedLog && viewMode !== 'list' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {logsLoading && !groupedData ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              {viewMode === 'byUser' && (
                <GroupedStatsTable
                  data={groupedData?.aiLogsGroupedByUser || []}
                  groupLabel={t('admin.user')}
                  onGroupClick={(groupId) => handleGroupClick('user', groupId)}
                />
              )}
              {viewMode === 'byType' && (
                <GroupedStatsTable
                  data={groupedData?.aiLogsGroupedByType || []}
                  groupLabel={t('admin.requestType')}
                  onGroupClick={(groupId) => handleGroupClick('type', groupId)}
                />
              )}
              {viewMode === 'byBook' && (
                <GroupedStatsTable
                  data={groupedData?.aiLogsGroupedByBook || []}
                  groupLabel={t('admin.book')}
                  onGroupClick={(groupId) => handleGroupClick('book', groupId)}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Filters - Only show in list view */}
      {!selectedLog && viewMode === 'list' && (
        <div className="flex flex-wrap gap-4 mb-6">
          <select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('admin.allUsers')}</option>
            {logsData?.usersWithLogs?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </select>

          <select
            value={filterBookId}
            onChange={(e) => setFilterBookId(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('admin.allBooks')}</option>
            {logsData?.booksWithLogs?.map((book) => (
              <option key={book.id} value={book.id}>
                {book.title?.substring(0, 40) || 'Untitled'}
              </option>
            ))}
          </select>

          <select
            value={filterRequestType}
            onChange={(e) => setFilterRequestType(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('admin.allTypes')}</option>
            {logsData?.aiPromptLogRequestTypes?.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          <select
            value={filterModelUsed}
            onChange={(e) => setFilterModelUsed(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('admin.allModels')}</option>
            <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</option>
            <option value="gemini-2.5-flash">gemini-2.5-flash</option>
            <option value="gemini-2.5-pro">gemini-2.5-pro</option>
            <option value="gemini-2.5-flash-image">gemini-2.5-flash-image</option>
            <option value="gemini-2.0-flash">gemini-2.0-flash</option>
            <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
          </select>

          <select
            value={filterSuccess}
            onChange={(e) => setFilterSuccess(e.target.value)}
            className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('admin.allStatuses')}</option>
            <option value="true">{t('admin.successful')}</option>
            <option value="false">{t('admin.failed')}</option>
          </select>

          {/* Clear filters button */}
          {(filterUserId || filterBookId || filterRequestType || filterModelUsed || filterSuccess) && (
            <button
              onClick={() => {
                setFilterUserId('');
                setFilterBookId('');
                setFilterRequestType('');
                setFilterModelUsed('');
                setFilterSuccess('');
              }}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              {t('common.clear')}
            </button>
          )}
        </div>
      )}

      {/* Logs Table - Only show in list view */}
      {!selectedLog && viewMode === 'list' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {logsLoading && !logsData ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : logsData?.aiPromptLogs?.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.requestType')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.user')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.book')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.input')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.output')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Words
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.cost')}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.duration')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.status')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('admin.createdAt')}
                      </th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {logsData.aiPromptLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                            {log.requestType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-[150px] truncate">
                          {log.userName || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-[150px] truncate">
                          {log.bookTitle || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-blue-600 dark:text-blue-400 text-right font-mono">
                          {formatTokens(log.inputTokens)}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400 text-right font-mono">
                          {formatTokens(log.outputTokens)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right font-mono">
                          {log.outputWordCount?.toLocaleString() || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-amber-600 dark:text-amber-400 text-right font-mono">
                          {log.estimatedCost ? `$${log.estimatedCost.toFixed(4)}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-right">
                          {log.durationMs ? `${log.durationMs.toLocaleString()}ms` : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {log.success ? (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                              {t('admin.successful')}
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded text-xs font-medium">
                              {t('admin.failed')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Link
                            to={`/admin/ai-prompt-logs?logId=${log.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              fetchLogDetail(log.id);
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {t('admin.viewDetails')}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {logsData.aiPromptLogs.length >= LOGS_LIMIT &&
                logsData.aiPromptLogs.length < logsData.aiPromptLogsStats.total && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-center">
                    <Button
                      variant="secondary"
                      onClick={() => fetchLogs(true)}
                      isLoading={logsLoading}
                    >
                      {t('admin.loadMore')}
                    </Button>
                  </div>
                )}
            </>
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {t('admin.noLogs')}
            </div>
          )}
        </div>
      )}

      {/* Requests by Type - Only show in list view when no detail selected */}
      {!selectedLog && viewMode === 'list' && logsData?.aiPromptLogsStats?.byType?.length ? (
        <div className="mt-6 p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('admin.requestsByType')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {logsData.aiPromptLogsStats.byType.map((item) => (
              <button
                key={item.requestType}
                onClick={() => setFilterRequestType(item.requestType)}
                className="p-3 text-left bg-gray-50 dark:bg-gray-900 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {item.requestType.replace(/_/g, ' ')}
                </div>
                <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {item.count.toLocaleString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
